import React, { useContext } from 'react';
import { Avatar, IconButton, Menu, MenuItem } from '@mui/material';
import { Link } from 'react-router-dom';
import { LoginContext } from '../../../App';
import { generateClassName, shouldRenderComponent } from '../../b2b-common/helpers';

import './IconMenu.css';
import useRolesAndProductsCheck from '../../b2b-common/hooks/useRolesAndProductsCheck';

/**
 * UserAvatar component - Displays the initials of the user's first and last name.
 * If userData is null, it displays a question mark.
 */
export function UserAvatar(className = null) {
    const { userData } = useContext(LoginContext);

    const getInitials = () => {
        if (!userData) {
            return "?";
        }
        const firstLetter = userData.firstName[0];
        const secondLetter = userData.lastName[0];
        return firstLetter + secondLetter;
    };

    return (
        <Avatar className={generateClassName("b2b-user-avatar", "-initials", className)}>{getInitials()}</Avatar>
    );
}

/**
 * IconMenu component - Displays a menu with options based on the user's roles and products.
 * 
 * @param {Object} props - The properties passed to the component.
 * @param {Object} props.icon - The icon to display for the menu.
 * @param {Array} props.options - The options to display in the menu.
 * @param {Array} props.rolesAllowed - The roles allowed to see the menu.
 * @param {Array} props.b2bProducts - The products allowed to see the menu.
 */
export default function IconMenu({ icon, options, rolesAllowed, b2bProducts, className = null }) {
    const [anchorEl, setAnchorEl] = React.useState(null);
    const { loginData } = useContext(LoginContext);
    const { checkRolesAndProductsPresence } = useRolesAndProductsCheck();

    //console.log('loginData', loginData);

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const shouldRender = shouldRenderComponent(loginData, rolesAllowed, b2bProducts);

    if (!shouldRender) {
        return null;
    }

    return (
        <div className={generateClassName("b2b-icon-menu", "", className)}>
            <IconButton onClick={handleClick} className={generateClassName("b2b-icon-menu", "-button", className)}>
                {icon}
            </IconButton>
            <Menu
                anchorEl={anchorEl}
                keepMounted
                open={Boolean(anchorEl)}
                onClose={handleClose}
            >
                {options.map((option, index) => {
                    const shouldRenderOption = checkRolesAndProductsPresence(option.roles || [], option.products || []);
                    return shouldRenderOption &&
                        <MenuItem
                            key={index}
                            onClick={(e) => {
                                if (option.onClick) option.onClick(e);
                                handleClose();
                            }}
                            component={option.path ? Link : 'li'}
                            to={option.path || undefined}
                            className={generateClassName("b2b-icon-menu", "menu-item", className)}
                        >
                            {option.icon}
                            {option.label}
                        </MenuItem>
                })}
            </Menu>
        </div>
    );
}